import React, { ComponentType } from 'react';
import { FormStateBasic } from 'informed';

import {
  AccountsRefreshAuthTokenProvider,
  PaymentsRefreshAuthTokenProvider
} from '@nw/shared/data-access';
import { SUCCESS } from '@nw/shared/utils';

import {
  PublicFormMachineSendType,
  PublicFormMachineStateType
} from '../../../form/machines/PublicFormsMachine/types';
import { CustomClassNamesInterface } from '../../../shared/types/MiscellaneousTypes';

import {
  usePublicFormsMachine,
  UsePublicMachineReturnType
} from '../../hooks/usePublicFormsMachine';
import {
  PublicProductFormMachineSendType,
  PublicProductFormMachineStateType
} from '../../machines/PublicFormsMachine/types';
import { DefaultProductTypes } from '../../types/ProductTypes';

interface FormMachineProps {
  publicFormsMachineSend: PublicProductFormMachineSendType;
  publicFormsMachineState: PublicProductFormMachineStateType;
  status: UsePublicMachineReturnType['status'];
  errors: UsePublicMachineReturnType['errors'];
}

interface WithPublicFormsMachineProps {
  productCode: string;
  renderCustomSuccessPage?: (
    formValues: FormStateBasic['values'],
    publicFormMachineState: PublicFormMachineStateType,
    publicFormMachineSend: PublicFormMachineSendType
  ) => React.ReactElement;
  localProductConfig?: DefaultProductTypes;
  shouldLoggedOutUser?: boolean;
  customClassNames?: CustomClassNamesInterface;
}

const withProviders = (WrappedComponent: ComponentType<any>) => {
  const WithProviders = (props: any): React.ReactElement => {
    return (
      <AccountsRefreshAuthTokenProvider>
        <PaymentsRefreshAuthTokenProvider>
          <WrappedComponent {...props} />
        </PaymentsRefreshAuthTokenProvider>
      </AccountsRefreshAuthTokenProvider>
    );
  };

  return WithProviders;
};

export const withPublicFormsMachine = (
  WrappedComponent: ComponentType<
    WithPublicFormsMachineProps & FormMachineProps
  >
) => {
  const WithPublicFormsMachine = (
    props: WithPublicFormsMachineProps
  ): React.ReactElement => {
    const { productCode, localProductConfig, shouldLoggedOutUser } = props;
    const {
      errors,
      status,
      publicFormsMachineState,
      publicFormsMachineSend
    }: UsePublicMachineReturnType = usePublicFormsMachine({
      productCode,
      localProductConfig,
      shouldLoggedOutUser
    });
    if (localProductConfig) {
      status.getInitialConfigStatus = SUCCESS;
    }
    return (
      <WrappedComponent
        {...props}
        publicFormsMachineSend={publicFormsMachineSend}
        publicFormsMachineState={publicFormsMachineState}
        status={status}
        errors={errors}
      />
    );
  };

  return withProviders(WithPublicFormsMachine);
};
